/* Product-page purchase controls. Both Complete the Look buttons use one state and request. */
(() => {
  'use strict';
  const controllers = new Map();
  const colorOption = name => /^(colou?r)$/i.test(String(name || '').trim());
  const normalize = value => String(value || '').trim().toLowerCase();

  function initialize(section) {
    if (controllers.has(section)) return;
    const root = section.querySelector('[data-section-type="product"]');
    const json = section.querySelector('script[id^="ProductJson-"]');
    if (!root || !json) return;
    const form = root.querySelector('form[data-product-form]');
    const nativeButton = form?.querySelector('[data-add-to-cart]');
    const look = root.querySelector('.cl-wrapper');
    const inlineButton = look?.querySelector('.cl-add-to-cart-btn');
    const bar = root.querySelector('[data-thl-sticky-cart]');
    if (!form || !nativeButton || (!inlineButton && !bar)) return;
    // Templates deliberately omitting purchase controls should remain without them.
    if (!inlineButton && form.classList.contains('visually-hidden')) return;
    const product = JSON.parse(json.textContent);
    const labels = JSON.parse(look?.querySelector('[data-cl-labels]')?.textContent || '{}');
    const label = (key, fallback) => labels[key] || fallback;
    const currency = (look || bar).dataset.currency || 'AUD';
    const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
      style: 'currency', currency, currencyDisplay: 'narrowSymbol'
    }).format(cents / 100);
    const abort = new AbortController();
    const listen = (node, event, handler, options = {}) => node.addEventListener(event, handler, {
      ...options, signal: abort.signal
    });
    const stickyButton = bar?.querySelector('[data-sticky-add-to-cart]');
    const inlineMessage = look?.querySelector('.cl-cart-message');
    const stickyMessage = bar?.querySelector('[data-sticky-message]');
    const nativeContainer = nativeButton.closest('.product-form__item--submit');
    const originalNativeDisplay = nativeContainer.style.display;
    const originalBarParent = bar?.parentNode;
    const spacer = document.createElement('div');
    spacer.className = 'thl-sticky-cart-spacer';
    spacer.setAttribute('aria-hidden', 'true');
    if (bar) {
      // Keep the fixed bar outside product animation/transform stacking contexts.
      document.body.append(bar, spacer);
    }
    if (inlineButton) nativeContainer.style.display = 'none';
    let busy = false;
    let frame = 0;
    let disposed = false;
    const items = Array.from(look?.querySelectorAll('.cl-item') || []).map(node => ({
      node,
      data: JSON.parse(node.querySelector('[data-cl-product]').textContent),
      checkbox: node.querySelector('.cl-checkbox'),
      groups: Array.from(node.querySelectorAll('[data-option-position]')),
      choices: {},
      multiplier: null,
      initialized: false,
      attempted: false,
      note: ''
    }));
    // Keep the native select as the single value source. Narrow screens use an
    // in-flow list so browser-native popups cannot drift or scale independently.
    const mobileQuery = window.matchMedia?.('(max-width: 767px)');
    const pickers = new Map();
    let openPicker;

    function closePicker(restoreFocus = false) {
      if (!openPicker) return;
      const picker = openPicker;
      openPicker = null;
      picker.list.hidden = true;
      picker.trigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus) picker.trigger.focus({ preventScroll: true });
    }

    function optionContent(option) {
      const content = document.createDocumentFragment();
      if (option.value && (option.dataset.swatchImage || option.dataset.swatchColor)) {
        const swatch = document.createElement('span');
        swatch.className = 'cl-mobile-swatch';
        swatch.setAttribute('aria-hidden', 'true');
        swatch.style.backgroundImage = option.dataset.swatchImage ? `url(${JSON.stringify(option.dataset.swatchImage)})` : '';
        swatch.style.backgroundColor = option.dataset.swatchColor || '';
        content.append(swatch);
      }
      const text = document.createElement('span');
      text.textContent = option.textContent;
      content.append(text);
      return content;
    }

    function syncPicker(select) {
      const picker = pickers.get(select);
      if (!picker) return;
      const selected = select.selectedOptions[0] || select.options[0];
      picker.value.replaceChildren(optionContent(selected));
      picker.trigger.setAttribute('aria-label', `${picker.name}: ${selected.textContent}`);
      picker.trigger.setAttribute('aria-invalid', select.getAttribute('aria-invalid') || 'false');
      Array.from(select.options).forEach((option, index) => {
        const button = picker.list.children[index];
        button.disabled = option.disabled;
        button.setAttribute('aria-selected', String(option.selected));
        button.replaceChildren(optionContent(option));
      });
    }

    function openOptions(picker, last = false) {
      if (!mobileQuery?.matches) return;
      closePicker();
      openPicker = picker;
      picker.list.hidden = false;
      picker.trigger.setAttribute('aria-expanded', 'true');
      const options = Array.from(picker.list.children).filter(button => !button.disabled);
      const target = last ? options.at(-1) : options.find(button => button.getAttribute('aria-selected') === 'true') || options[0];
      target?.focus({ preventScroll: true });
      target?.scrollIntoView({ block: 'nearest' });
    }

    items.forEach((item, itemIndex) => {
      if (!mobileQuery) return;
      item.node.querySelectorAll('.cl-select, .cl-quantity-select').forEach((select, index) => {
        const wrapper = select.parentNode;
        const host = document.createElement('div');
        host.className = 'cl-mobile-picker';
        const trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'cl-mobile-trigger';
        trigger.id = `${select.id || `cl-picker-${itemIndex}-${index}`}-trigger`;
        trigger.setAttribute('aria-haspopup', 'listbox');
        trigger.setAttribute('aria-expanded', 'false');
        const value = document.createElement('span');
        value.className = 'cl-mobile-value';
        trigger.append(value);
        const chevron = wrapper.querySelector('.cl-select-chevron')?.cloneNode(true);
        if (chevron) trigger.append(chevron);
        const list = document.createElement('div');
        list.className = 'cl-mobile-options';
        list.id = `${select.id || `cl-picker-${itemIndex}-${index}`}-listbox`;
        list.setAttribute('role', 'listbox');
        list.hidden = true;
        trigger.setAttribute('aria-controls', list.id);
        const name = select.labels?.[0]?.textContent || select.getAttribute('aria-label') ||
          (select.matches('.cl-quantity-select') ? 'Quantity' : item.data.options[Number(select.closest('[data-option-position]')?.dataset.optionPosition)]);
        list.setAttribute('aria-label', name);
        const picker = { select, host, trigger, value, list, name, originalTabIndex: select.getAttribute('tabindex'), originalAriaHidden: select.getAttribute('aria-hidden') };
        Array.from(select.options).forEach(option => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'cl-mobile-option';
          button.setAttribute('role', 'option');
          button.tabIndex = -1;
          listen(button, 'click', () => {
            if (button.disabled) return;
            select.value = option.value;
            closePicker(true);
            select.dispatchEvent(new Event('change', { bubbles: true }));
          });
          list.append(button);
        });
        host.append(trigger, list);
        wrapper.append(host);
        wrapper.classList.add('has-mobile-picker');
        pickers.set(select, picker);
        listen(trigger, 'click', () => openPicker === picker ? closePicker() : openOptions(picker));
        listen(trigger, 'keydown', event => {
          if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
            event.preventDefault();
            openOptions(picker, event.key === 'ArrowUp');
          }
        });
        listen(list, 'keydown', event => {
          if (event.key === 'Escape') { event.preventDefault(); closePicker(true); return; }
          if (event.key === 'Tab') { closePicker(true); return; }
          if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const options = Array.from(list.children).filter(button => !button.disabled);
          const current = options.indexOf(document.activeElement);
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 :
            (current + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
          options[next]?.focus();
        });
      });
    });
    function syncPickerMode() {
      closePicker();
      pickers.forEach(picker => {
        if (mobileQuery?.matches) {
          picker.select.tabIndex = -1;
          picker.select.setAttribute('aria-hidden', 'true');
        } else {
          for (const [attribute, value] of [['tabindex', picker.originalTabIndex], ['aria-hidden', picker.originalAriaHidden]]) {
            if (value === null) picker.select.removeAttribute(attribute);
            else picker.select.setAttribute(attribute, value);
          }
        }
        const label = look?.querySelector(`label[for="${picker.select.id}"]`) || look?.querySelector(`label[for="${picker.trigger.id}"]`);
        if (label) label.htmlFor = mobileQuery?.matches ? picker.trigger.id : picker.select.id;
      });
    }
    if (mobileQuery) listen(mobileQuery, 'change', syncPickerMode);
    syncPickerMode();
    listen(document, 'click', event => {
      if (openPicker && !openPicker.host.contains(event.target)) closePicker();
    });
    listen(document, 'focusin', event => {
      if (openPicker && !openPicker.host.contains(event.target)) closePicker();
    });

    function mainVariant() {
      // Option inputs are authoritative, including combinations without a variant.
      const controls = Array.from(root.querySelectorAll('[name^="options"]'))
        .filter(input => !input.closest('.cl-wrapper') && input.closest('[data-section-type="product"]') === root);
      if (controls.length) {
        const choices = new Map();
        controls.forEach(input => {
          if (!['radio', 'checkbox'].includes(input.type) || input.checked) choices.set(input.name, input.value);
        });
        return product.variants.find(variant => product.options.every((option, index) =>
          variant.options[index] === choices.get(`options[${typeof option === 'string' ? option : option.name}]`)));
      }
      return product.variants.find(variant => String(variant.id) === String(new FormData(form).get('id')));
    }

    function mainQuantity() {
      const quantityInput = root.querySelector('[data-quantity-input]') || form.querySelector('[name="quantity"]');
      return Number(quantityInput?.value ?? 1);
    }

    function selectedVariant(item) {
      if (item.data.defaultVariant) return item.data.variants[0];
      return item.data.variants.find(variant => item.data.options.every((_, index) =>
        item.choices[index] !== undefined && variant.options[index] === item.choices[index]));
    }

    function completeItem(item) {
      return Boolean(selectedVariant(item)?.available &&
        (!item.node.querySelector('.cl-pcs-selection') || item.multiplier));
    }

    function initializeItem(item) {
      if (item.initialized) return;
      item.initialized = true;
      const mainColorIndex = product.options.findIndex(option => colorOption(typeof option === 'string' ? option : option.name));
      const color = mainVariant()?.options[mainColorIndex];
      item.groups.forEach(group => {
        const index = Number(group.dataset.optionPosition);
        if (!colorOption(item.data.options[index]) || !color) return;
        const match = item.data.variants.find(variant => variant.available && normalize(variant.options[index]) === normalize(color));
        if (match) item.choices[index] = match.options[index];
      });
    }

    function optionAvailable(item, index, value) {
      return item.data.variants.some(variant => variant.available && variant.options[index] === value &&
        (colorOption(item.data.options[index]) || item.data.options.every((name, position) =>
          position === index || (!colorOption(name) && position > index) || item.choices[position] === undefined ||
          variant.options[position] === item.choices[position])));
    }

    function changeOption(item, index, value) {
      if (value) item.choices[index] = value;
      else delete item.choices[index];
      item.note = '';
      item.groups.forEach(group => {
        const position = Number(group.dataset.optionPosition);
        if (position === index || colorOption(item.data.options[position]) || item.choices[position] === undefined) return;
        if (!optionAvailable(item, position, item.choices[position])) {
          delete item.choices[position];
          item.note = label('sizeReset', 'This option is unavailable in your chosen colour. Please choose again.');
        }
      });
    }

    function expandItem(item, expanded) {
      if (expanded) {
        items.forEach(other => {
          if (other !== item && other.node.classList.contains('is-expanded')) expandItem(other, false);
        });
      } else {
        if (openPicker && item.node.contains(openPicker.host)) closePicker();
        // Only completed, available additions remain selected when put away.
        // Keep their partial choices so reopening never discards the work.
        if (!completeItem(item)) {
          item.checkbox.checked = false;
          item.attempted = false;
          item.note = '';
        }
      }
      item.node.querySelector('.cl-variants').hidden = !expanded;
      item.node.querySelector('.cl-toggle').setAttribute('aria-expanded', String(expanded));
      item.node.classList.toggle('is-expanded', expanded);
    }

    function renderItem(item) {
      item.groups.forEach(group => {
        const index = Number(group.dataset.optionPosition);
        const select = group.querySelector('.cl-select');
        Array.from(select.options).forEach(option => {
          if (!option.value) return;
          const available = optionAvailable(item, index, option.value);
          option.disabled = !available;
          option.textContent = available ? option.value : `${option.value} — ${label('soldOut', 'Sold out')}`;
        });
        select.value = item.choices[index] || '';
        select.setAttribute('aria-invalid', String(item.attempted && item.checkbox.checked && !select.value));
        syncPicker(select);
        const swatch = group.querySelector('.cl-colour-swatch');
        if (swatch) {
          const option = select.selectedOptions[0];
          swatch.hidden = !select.value;
          swatch.style.backgroundImage = option?.dataset.swatchImage ? `url(${JSON.stringify(option.dataset.swatchImage)})` : '';
          swatch.style.backgroundColor = option?.dataset.swatchColor || '';
          select.parentNode.classList.toggle('has-colour', Boolean(select.value));
        }
      });
      const quantitySelect = item.node.querySelector('.cl-quantity-select');
      if (quantitySelect) {
        quantitySelect.value = item.multiplier ? String(item.multiplier) : '';
        quantitySelect.setAttribute('aria-invalid', String(item.attempted && item.checkbox.checked && !item.multiplier));
        syncPicker(quantitySelect);
      }
      const note = item.node.querySelector('.cl-item-note');
      if (note) { note.textContent = item.note; note.hidden = !item.note; }
      const summary = item.node.querySelector('.cl-summary');
      if (summary) {
        summary.textContent = [...item.data.options.map((_, index) => item.choices[index]).filter(Boolean),
          quantitySelect?.selectedOptions[0]?.value ? quantitySelect.selectedOptions[0].textContent : ''].filter(Boolean).join(' · ');
        summary.hidden = !item.checkbox.checked || !summary.textContent;
      }
      const variant = selectedVariant(item);
      const price = item.node.querySelector('.cl-price');
      const complete = item.checkbox.checked && completeItem(item);
      price.classList.toggle('selected', Boolean(complete));
      price.textContent = complete ? money(variant.price * (item.multiplier || 1)) : `${label('from', 'From')} ${money(Number(price.dataset.basePrice))}`;
      if (complete && variant.compare_at_price > variant.price) {
        const current = document.createElement('span');
        current.className = 'cl-price-current';
        current.textContent = price.textContent;
        const compare = document.createElement('span');
        compare.className = 'cl-price-compare';
        compare.textContent = money(variant.compare_at_price * (item.multiplier || 1));
        price.replaceChildren(current, compare);
      }
    }

    function summaryImage(variant, fallback) {
      const source = variant?.featured_image?.src || fallback?.src || fallback;
      if (!source || typeof source !== 'string') return '';
      try {
        const url = new URL(source, window.location.href);
        if (!['https:', 'http:'].includes(url.protocol)) return '';
        url.searchParams.set('width', '80');
        return url.href;
      } catch (_) { return ''; }
    }

    function snapshot() {
      const variant = mainVariant();
      const quantity = mainQuantity();
      const lines = [];
      const summary = [];
      let invalidItem;
      items.forEach(item => {
        renderItem(item);
        if (!item.checkbox.checked) return;
        const selected = selectedVariant(item);
        const missingQuantity = item.node.querySelector('.cl-pcs-selection') && !item.multiplier;
        const status = !selected ? label('chooseOptions', 'Please select options') : !selected.available ? label('soldOut', 'Sold out') :
          missingQuantity ? label('chooseQuantity', 'Please select quantity') : '';
        const count = item.multiplier || 1;
        const pillowcases = item.node.dataset.pcsType === 'pillowcases';
        const european = item.node.dataset.pcsType === 'european';
        summary.push({
          title: item.data.title,
          image: summaryImage(selected, item.data.image),
          options: item.data.defaultVariant ? '' : item.data.options.map((_, index) => item.choices[index]).filter(Boolean).join(' / '),
          quantity: missingQuantity ? '' : pillowcases ? `${count} ${count === 1 ? 'pack' : 'packs'} (${count * 2} pillowcases)` : european ? `${count} ${count === 1 ? 'pillowcase' : 'pillowcases'}` : `×${count}`,
          status,
          subtotal: status ? null : selected.price * count
        });
        if (!selected?.available || missingQuantity) {
          invalidItem ||= item;
        } else {
          lines.push({ id: selected.id, quantity: item.multiplier || 1, price: selected.price });
        }
      });
      const validQuantity = Number.isInteger(quantity) && quantity > 0;
      const total = (variant?.price || 0) * (validQuantity ? quantity : 0) +
        lines.reduce((sum, line) => sum + line.price * line.quantity, 0);
      if (summary.length) summary.unshift({
        title: product.title,
        image: summaryImage(variant, product.featured_image),
        options: variant?.options.filter(option => option !== 'Default Title').join(' / ') || '',
        quantity: validQuantity ? `×${quantity}` : '',
        status: !variant ? 'Please select options' : !variant.available ? 'Sold out' : !validQuantity ? 'Please select quantity' : '',
        subtotal: variant && validQuantity ? variant.price * quantity : null
      });
      return { variant, quantity, validQuantity, lines, summary, invalidItem, total };
    }

    function setButton(button, label, disabled) {
      if (!button) return;
      if (button.textContent !== label) button.textContent = label;
      button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
      button.setAttribute('aria-busy', String(busy));
    }

    function syncSizing() {
      form.querySelector('thl-nz-sizing-guard')?.sync?.(true);
    }

    function render() {
      if (disposed) return;
      const state = snapshot();
      syncSizing();
      const unavailable = !state.variant?.available;
      const label = busy ? 'Adding to cart…' : unavailable ? (state.variant ? 'Sold out' : 'Unavailable') :
        `${nativeButton.dataset.langAvailable || 'ADD TO CART'} - ${money(state.total)}`;
      setButton(inlineButton, label, busy || unavailable);
      // Native purchase continues through the theme's own form handler.
      const nativeBusy = !inlineButton && nativeButton.classList.contains('loading');
      setButton(stickyButton, nativeBusy ? 'Adding to cart…' : label,
        busy || unavailable || (!inlineButton && (nativeButton.disabled || nativeBusy)));
      if (bar) {
        bar.querySelector('[data-sticky-price]').textContent = money(state.total);
        bar.querySelector('[data-sticky-compare-price]').textContent = '';
        bar.querySelector('[data-bar-quantity]').textContent = state.validQuantity ? `× ${state.quantity}` : '';
        bar.querySelector('[data-bar-product-title]').textContent = state.variant?.name || product.title;
        const list = bar.querySelector('[data-sticky-items]');
        if (list) {
          bar.classList.toggle('has-selected-items', state.summary.length > 0);
          list.hidden = !state.summary.length;
          // Reuse the snapshot used for totals and submission; names remain plain text.
          const rows = state.summary.map(row => {
            const li = document.createElement('li');
            if (row.image) {
              const image = document.createElement('img');
              image.className = 'thl-sticky-cart__item-image';
              image.src = row.image;
              image.alt = '';
              image.width = 40;
              image.height = 40;
              image.decoding = 'async';
              li.append(image);
            } else li.classList.add('thl-sticky-cart__item--no-image');
            const heading = document.createElement('div');
            heading.className = 'thl-sticky-cart__item-heading';
            const title = document.createElement('span');
            title.className = 'thl-sticky-cart__item-title';
            title.textContent = row.title;
            const details = document.createElement('span');
            details.className = 'thl-sticky-cart__item-details';
            heading.append(title);
            if (row.options) {
              const options = document.createElement('span');
              options.className = 'thl-sticky-cart__item-options';
              options.textContent = ` — ${row.options}`;
              heading.append(options);
            }
            details.textContent = [row.quantity, row.status].filter(Boolean).join(' · ');
            li.append(heading, details);
            if (row.subtotal !== null && !row.status) {
              const price = document.createElement('strong');
              price.textContent = money(row.subtotal);
              details.append(price);
            }
            return li;
          });
          // Avoid rebuilding unchanged content, preserving scroll position during requests.
          const signature = JSON.stringify(state.summary);
          if (list.dataset.summary !== signature) {
            list.replaceChildren(...rows);
            list.dataset.summary = signature;
          }
        }
        updateVisibility();
      }
    }

    function scheduleRender() {
      if (!frame) frame = requestAnimationFrame(() => { frame = 0; render(); });
    }

    function showMessage(text, error = false) {
      [inlineMessage, stickyMessage].filter(Boolean).forEach(node => {
        node.textContent = text;
        node.hidden = !text;
        node.style.display = text ? 'block' : 'none';
        node.classList.toggle('error', error);
        node.classList.toggle('success', !error);
      });
      updateVisibility();
    }

    function focusIncomplete(item) {
      item.attempted = true;
      expandItem(item, true);
      renderItem(item);
      const missing = item.groups.find(group => item.choices[Number(group.dataset.optionPosition)] === undefined);
      const target = missing?.querySelector('select') ||
        (!item.multiplier && item.node.querySelector('.cl-quantity-select')) || item.node.querySelector('.cl-toggle');
      item.node.scrollIntoView({ block: 'center', behavior: 'smooth' });
      (mobileQuery?.matches && pickers.get(target)?.trigger || target)?.focus({ preventScroll: true });
    }

    async function submitLook() {
      if (busy) return;
      const state = snapshot();
      if (!state.variant?.available) {
        showMessage('Please choose an available product variant.', true);
        render();
        return;
      }
      if (!state.validQuantity) {
        showMessage('Please enter a whole quantity of at least 1.', true);
        const input = root.querySelector('[data-quantity-input]') || form.querySelector('[name="quantity"]');
        input?.scrollIntoView({ block: 'center' });
        input?.focus();
        return;
      }
      if (state.invalidItem) {
        showMessage(label('incomplete', 'Please complete the options and quantity for each selected Complete the Look item.'), true);
        focusIncomplete(state.invalidItem);
        return;
      }
      syncSizing();
      const sizingGuard = form.querySelector('thl-nz-sizing-guard');
      if (sizingGuard && !sizingGuard.ensureConfirmed()) return;
      if (!form.reportValidity()) return;
      // FormData retains line-item properties (including files) and selling plans.
      const payload = new FormData();
      payload.set('items[0][id]', String(state.variant.id));
      payload.set('items[0][quantity]', String(state.quantity));
      for (const [name, value] of new FormData(form)) {
        if (name.startsWith('properties[')) payload.append(`items[0][${name.replace('[', '][')}`, value);
        else if (name === 'selling_plan' && value) payload.set('items[0][selling_plan]', value);
      }
      state.lines.forEach((line, index) => {
        payload.set(`items[${index + 1}][id]`, String(line.id));
        payload.set(`items[${index + 1}][quantity]`, String(line.quantity));
      });
      busy = true;
      showMessage('');
      render();
      const requestAbort = new AbortController();
      let requestTimeout;
      try {
        await Promise.race([
          (async () => {
            const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
              method: 'POST', body: payload, signal: requestAbort.signal
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.description || data.message || 'Unable to add these items. Please check availability and try again.');
          })(),
          new Promise((_, reject) => {
            requestTimeout = setTimeout(() => {
              reject(new Error('The request timed out. Please check your cart before trying again.'));
              requestAbort.abort();
            }, 25000);
          })
        ]);
        if (disposed) return;
        showMessage('Added to cart.');
        // Existing theme hook refreshes the drawer and header cart count.
        document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
      } catch (error) {
        if (!disposed) showMessage(error instanceof TypeError ? 'Unable to connect. Please check your cart before trying again.' : error.message, true);
      } finally {
        clearTimeout(requestTimeout);
        busy = false;
        if (!disposed) render();
      }
    }

    items.forEach(item => {
      expandItem(item, false);
      listen(item.checkbox, 'change', () => {
        if (item.checkbox.checked) initializeItem(item);
        expandItem(item, item.checkbox.checked);
        showMessage('');
        render();
      });
      listen(item.node, 'change', event => {
        if (event.target.matches('.cl-select')) {
          const index = Number(event.target.closest('[data-option-position]').dataset.optionPosition);
          changeOption(item, index, event.target.value);
        } else if (event.target.matches('.cl-quantity-select')) {
          const value = Number(event.target.value);
          item.multiplier = [1, 2, 3, 4].includes(value) &&
            Array.from(event.target.options).some(option => Number(option.value) === value) ? value : null;
        } else return;
        showMessage('');
        render();
      });
      listen(item.node, 'click', event => {
        if (event.target.closest('.cl-item-main') && event.target !== item.checkbox) {
          const expanded = item.node.querySelector('.cl-toggle').getAttribute('aria-expanded') !== 'true';
          if (expanded) {
            item.checkbox.checked = true;
            initializeItem(item);
          }
          expandItem(item, expanded);
        } else return;
        showMessage('');
        render();
      });
    });
    if (inlineButton) {
      listen(inlineButton, 'click', submitLook);
      // Prevent hidden native/Enter submissions from bypassing the selected additions.
      listen(form, 'submit', event => {
        event.preventDefault();
        event.stopImmediatePropagation();
        submitLook();
      }, { capture: true });
    }
    if (stickyButton) listen(stickyButton, 'click', () => {
      if (inlineButton) submitLook();
      else if (!nativeButton.disabled && !nativeButton.classList.contains('loading')) {
        nativeButton.click();
        scheduleRender();
      }
    });
    listen(root, 'change', scheduleRender, { capture: true });
    listen(root, 'input', scheduleRender, { capture: true });
    // Theme updates hidden form values programmatically, so observe its visible button too.
    const nativeObserver = new MutationObserver(scheduleRender);
    nativeObserver.observe(nativeButton, { attributes: true, childList: true, subtree: true, characterData: true });
    listen(document, 'product:quantity-update', event => {
      if (product.variants.some(variant => variant.id === event.detail?.variant?.id)) scheduleRender();
    });
    const quantityError = root.querySelector('[data-quantity-error]');
    const errorObserver = new MutationObserver(() => {
      if (!inlineButton && quantityError && !quantityError.classList.contains('hidden')) {
        showMessage(quantityError.textContent.trim(), true);
      }
    });
    if (quantityError) errorObserver.observe(quantityError, { attributes: true, childList: true, subtree: true });

    function updateVisibility() {
      if (!bar || disposed) return;
      const button = inlineButton || nativeButton;
      const rect = button.getBoundingClientRect();
      const visible = rect.height > 0 && rect.bottom <= 0 && !document.body.classList.contains('scroll-lock');
      bar.hidden = !visible;
      bar.classList.toggle('active', visible);
      const height = visible ? `${bar.getBoundingClientRect().height}px` : '0px';
      spacer.style.height = height;
      document.body.style.setProperty('--thl-sticky-cart-height', height);
    }
    const intersection = new IntersectionObserver(updateVisibility);
    if (bar) intersection.observe(inlineButton || nativeButton);
    const resize = new ResizeObserver(updateVisibility);
    if (bar) resize.observe(bar);
    const bodyObserver = new MutationObserver(updateVisibility);
    if (bar) bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    listen(window, 'scroll', updateVisibility, { passive: true });
    listen(window, 'resize', updateVisibility, { passive: true });
    render();
    controllers.set(section, () => {
      disposed = true;
      abort.abort();
      cancelAnimationFrame(frame);
      nativeObserver.disconnect();
      errorObserver.disconnect();
      intersection.disconnect();
      resize.disconnect();
      bodyObserver.disconnect();
      closePicker();
      pickers.forEach(picker => {
        picker.host.remove();
        picker.select.parentNode.classList.remove('has-mobile-picker');
        const label = look?.querySelector(`label[for="${picker.trigger.id}"]`);
        if (label) label.htmlFor = picker.select.id;
        for (const [attribute, value] of [['tabindex', picker.originalTabIndex], ['aria-hidden', picker.originalAriaHidden]]) {
          if (value === null) picker.select.removeAttribute(attribute);
          else picker.select.setAttribute(attribute, value);
        }
      });
      nativeContainer.style.display = originalNativeDisplay;
      if (bar) { bar.hidden = true; originalBarParent.append(bar); }
      spacer.remove();
      document.body.style.removeProperty('--thl-sticky-cart-height');
      controllers.delete(section);
    });
  }

  function mount(scope) {
    if (scope.matches?.('.shopify-section')) initialize(scope);
    else scope.querySelectorAll('.shopify-section').forEach(initialize);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  else mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => controllers.get(event.target)?.());
})();
